import { computed, ref, watch } from 'vue'
import type { ComputedRef } from 'vue'

import type { DrawerGraphEntry } from '@app/application/report/types'

export interface UseExpandedSetReturn {
  expanded: ComputedRef<DrawerGraphEntry[]>
  row: ComputedRef<DrawerGraphEntry[]>
  onToggle: (id: number, value: boolean) => void
}

/**
 * Splits a list of drawer entries into "expanded" (lifted above) and "row"
 * (collapsed) buckets, driven by an internal Set of ids. The drawer's
 * highlighted and insight sections both use this to render expanded pills
 * full-width above their layout grid.
 */
export function useExpandedSet(
  entries: () => DrawerGraphEntry[]
): UseExpandedSetReturn {
  const expandedIds = ref<Set<number>>(new Set())

  const expanded = computed(() =>
    entries().filter((entry) => expandedIds.value.has(entry.id))
  )

  const row = computed(() =>
    entries().filter((entry) => !expandedIds.value.has(entry.id))
  )

  // Drop ids whose entries left the source array (e.g. user highlighted an
  // expanded insight pill — the id stays stale otherwise and the pill would
  // auto-re-expand if the same graph re-enters the array later).
  watch(
    () => entries().map((entry) => entry.id),
    (currentIds) => {
      const valid = new Set(currentIds)
      let changed = false
      const next = new Set<number>()
      for (const id of expandedIds.value) {
        if (valid.has(id)) next.add(id)
        else changed = true
      }
      if (changed) expandedIds.value = next
    }
  )

  function onToggle(id: number, value: boolean): void {
    const next = new Set(expandedIds.value)
    if (value) next.add(id)
    else next.delete(id)
    expandedIds.value = next
  }

  return { expanded, row, onToggle }
}
